import * as React from 'react'
import { useForm } from 'react-hook-form'
import { parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import {
  SearchResultForm as Presenter,
  IProps as IPresenter,
} from './presenter'
import {
  makeSearchConditionTrackingData,
  makeSearchScheduleTrackingData,
  makeSearchTrackingGroup,
} from '~/organisms/search/SearchCondition'
import {
  trackingUserContext,
  searchEventIdContext,
  ISearchEvents,
} from '@/contexts/trackingContexts'
import { executeSearch, executeRegularSearch } from '@/utils/search'
import {
  SEARCH_EVENT_NAME_PAGE_VIEW,
  SEARCH_EVENT_NAME_MODAL_VIEW,
  SEARCH_EVENT_NAME_CLICK,
  SEARCH_EVENT_CATEGORY_SEARCH_RESULT,
  SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SORT,
  SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
  SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT,
  SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SORT,
  SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
  SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT,
  SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SORT,
  SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
  trackEvent,
} from '@/utils/analyze'

interface IProps extends IPresenter {
  isAreaSearch?: boolean
  isRegulerSearch?: boolean
  user?: ITrackingUser
  searchParams: ISearchState
  reserveType: IReserveType
  selectedDateString?: string
  selectedGeocodes: {
    [key: number]: IGeocode[]
  }
  selectedStations: {
    [key: number]: IStation[]
  }
  sitterPer?: number
  sitterOffset?: number
}
const defaultEventIds: ISearchEvents = {
  SEARCH_RESULT_VIEW: {
    name: SEARCH_EVENT_NAME_PAGE_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT,
  },
  SEARCH_RESULT_FILTER_DELETE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '絞込み条件クリア',
  },
  SEARCH_RESULT_CLICK_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SORT,
  },
  SEARCH_RESULT_CHANGE_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SORT,
  },
  // 日付選択モーダル
  SEARCH_MODAL_DATE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '日付条件',
  },
  SEARCH_MODAL_DATE_CLICK_TODAY: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '今日',
  },
  SEARCH_MODAL_DATE_CLICK_TOMORROW: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '明日',
  },
  SEARCH_MODAL_DATE_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '閉じる',
  },
  SEARCH_MODAL_DATE_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '決定',
  },
  // 時間選択モーダル
  SEARCH_MODAL_TIME_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '時間条件',
  },
  SEARCH_MODAL_TIME_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '閉じる',
  },
  SEARCH_MODAL_TIME_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '検索',
  },
  // 検索条件モーダル
  SEARCH_MODAL_CONDITION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
  },
  SEARCH_MODAL_CONDITION_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '閉じる',
  },
  SEARCH_MODAL_CONDITION_RESET: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '条件クリア',
  },
  SEARCH_MODAL_CONDITION_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '検索する',
  },
  // 都道府県モーダル
  SEARCH_MODAL_PREFECTURE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_都道府県',
  },
  // エリアモーダル
  SEARCH_MODAL_AREA_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_市区町村',
  },
  // 駅モーダル
  SEARCH_MODAL_STATION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_駅',
  },
}

const areaEventIds: ISearchEvents = {
  SEARCH_RESULT_VIEW: {
    name: SEARCH_EVENT_NAME_PAGE_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT,
  },
  SEARCH_RESULT_FILTER_DELETE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '絞込み条件クリア',
  },
  SEARCH_RESULT_CLICK_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SORT,
  },
  SEARCH_RESULT_CHANGE_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SORT,
  },
  // 日付選択モーダル
  SEARCH_MODAL_DATE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '日付条件',
  },
  SEARCH_MODAL_DATE_CLICK_TODAY: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '今日',
  },
  SEARCH_MODAL_DATE_CLICK_TOMORROW: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '明日',
  },
  SEARCH_MODAL_DATE_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '閉じる',
  },
  SEARCH_MODAL_DATE_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '決定',
  },
  // 時間選択モーダル
  SEARCH_MODAL_TIME_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '時間条件',
  },
  SEARCH_MODAL_TIME_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '閉じる',
  },
  SEARCH_MODAL_TIME_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '検索',
  },
  // 検索条件モーダル
  SEARCH_MODAL_CONDITION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
  },
  SEARCH_MODAL_CONDITION_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '閉じる',
  },
  SEARCH_MODAL_CONDITION_RESET: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '条件クリア',
  },
  SEARCH_MODAL_CONDITION_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '検索する',
  },
  // 都道府県モーダル
  SEARCH_MODAL_PREFECTURE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_都道府県',
  },
  // エリアモーダル
  SEARCH_MODAL_AREA_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_市区町村',
  },
  // 駅モーダル
  SEARCH_MODAL_STATION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_AREA_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_駅',
  },
}

const regularEventIds: ISearchEvents = {
  SEARCH_RESULT_VIEW: {
    name: SEARCH_EVENT_NAME_PAGE_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT,
  },
  SEARCH_RESULT_FILTER_DELETE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '絞込み条件クリア',
  },
  SEARCH_RESULT_CLICK_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SORT,
  },
  SEARCH_RESULT_CHANGE_ORDER: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SORT,
  },
  // 日付選択モーダル
  SEARCH_MODAL_DATE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '日付条件',
  },
  SEARCH_MODAL_DATE_CLICK_TODAY: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '今日',
  },
  SEARCH_MODAL_DATE_CLICK_TOMORROW: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '明日',
  },
  SEARCH_MODAL_DATE_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '閉じる',
  },
  SEARCH_MODAL_DATE_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '日付条件',
    label: '決定',
  },
  // 時間選択モーダル
  SEARCH_MODAL_TIME_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '時間条件',
  },
  SEARCH_MODAL_TIME_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '閉じる',
  },
  SEARCH_MODAL_TIME_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '時間条件',
    label: '検索',
  },
  // 検索条件モーダル
  SEARCH_MODAL_CONDITION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
  },
  SEARCH_MODAL_CONDITION_CLOSE: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '閉じる',
  },
  SEARCH_MODAL_CONDITION_RESET: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '条件クリア',
  },
  SEARCH_MODAL_CONDITION_SUBMIT: {
    name: SEARCH_EVENT_NAME_CLICK,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: '絞込み条件',
    label: '検索する',
  },
  // 都道府県モーダル
  SEARCH_MODAL_PREFECTURE_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_都道府県',
  },
  // エリアモーダル
  SEARCH_MODAL_AREA_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_市区町村',
  },
  // 駅モーダル
  SEARCH_MODAL_STATION_VIEW: {
    name: SEARCH_EVENT_NAME_MODAL_VIEW,
    category: SEARCH_EVENT_CATEGORY_SEARCH_REGULAR_RESULT_SEARCH_CONDITION,
    group: 'エリア条件_駅',
  },
}

export const SearchResultForm = ({
  isAreaSearch,
  isRegulerSearch,
  user,
  reserveType,
  searchParams,
  selectedDateString,
  selectedGeocodes,
  selectedStations,
  sortOrderList,
  ...props
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const [modalType, setModal] = React.useState('')
  const [searchResultFormSummariesFlex, setSearchResultFormSummariesFlex] =
    React.useState(false)
  const sittersViewFromCount = Number(props.sitterOffset) + 1
  const defaltSearchPre =
    searchParams.search.search_type == 'housekeeping' ? 12 : 20
  const searchPre =
    Number(props.sitterPer) > 0 ? Number(props.sitterPer) : defaltSearchPre
  const [sittersViewToCount, setSittersViewToCount] = React.useState(
    props.sittersTotalCount > Number(props.sitterOffset) + searchPre
      ? Number(props.sitterOffset) + searchPre
      : props.sittersTotalCount,
  )
  const { getValues, watch } = useForm<ISearchState>({
    defaultValues: {
      reserveType,
      search: {
        ...searchParams.search,
        geocode: selectedGeocodes ? selectedGeocodes : {},
        station: selectedStations ? selectedStations : {},
        work_date_from: selectedDateString
          ? parseISO(selectedDateString)
          : undefined,
        work_time_from: searchParams.search.work_time_from
          ? searchParams.search.work_time_from
          : '',
        work_time_to: searchParams.search.work_time_to
          ? searchParams.search.work_time_to
          : '',
      },
      regular_search: searchParams.regular_search
        ? searchParams.regular_search
        : {},
      pageType: 'searchResultForm',
    },
  })
  let eventIds: ISearchEvents = {}
  if (isAreaSearch) {
    eventIds = areaEventIds
  } else if (isRegulerSearch) {
    eventIds = regularEventIds
  } else {
    eventIds = defaultEventIds
  }

  const watchAllFields = watch()
  const handleScroll = () => {
    const searchHeader = document.getElementsByClassName(
      'search-sp-header-ver2-block',
    )[0]
    const rect = searchHeader.getBoundingClientRect()
    rect.top <= 0
      ? setSearchResultFormSummariesFlex(true)
      : setSearchResultFormSummariesFlex(false)
  }

  const sittersTotalCount = props.sittersTotalCount
  const handleViewCount = () => {
    if (sittersTotalCount > 0) {
      const spV1Count =
        document
          ?.getElementsByClassName('search-sitter-list-area')[0]
          ?.getElementsByClassName('search-list-sitter-block')?.length / 2 // PC・SP両方をカウントしているので1/2とする
      const spV2Count = document
        ?.getElementsByClassName('search-sitter-list-area')[0]
        ?.getElementsByClassName('base-result-bg')[0]
        ?.getElementsByClassName('sitter-profile-card')?.length
      setSittersViewToCount(
        Number(props.sitterOffset) +
          Math.max(spV1Count ? spV1Count : 0, spV2Count ? spV2Count : 0),
      )
    }
  }

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  React.useEffect(() => {
    window.addEventListener('scroll', handleViewCount)
    return () => window.removeEventListener('scroll', handleViewCount)
  }, [])

  const handleSubmit = ({
    search,
    regular_search,
    reserveType,
  }: ISearchState) => {
    reserveType === 'normal'
      ? executeSearch({ search })
      : executeRegularSearch({ search, regular_search })
  }

  /*------------------ ソート条件変更 ------------------*/
  const searchOrderClickHandler = () => {
    trackEvent(eventIds.SEARCH_RESULT_CLICK_ORDER, user)
  }

  if (
    props.isLogin &&
    !sortOrderList.some((sortKey) => sortKey[1] === 'live_nearby')
  ) {
    sortOrderList.push(['自宅から近い順', 'live_nearby'])
  }

  const searchOrderChangeHandler = (changed_order: ISearchOrderType) => {
    const order = sortOrderList.find((order) => order[1] === changed_order)
    const items = [{ itemId: `search_order_${order[1]}`, itemName: order[0] }]
    trackEvent({ ...eventIds.SEARCH_RESULT_CHANGE_ORDER, items }, user)

    const search = getValues('search')
    search['order'] = changed_order
    const regulerSearch = getValues('regular_search')
    const reserveType = getValues('reserveType')
    handleSubmit({ search, regular_search: regulerSearch, reserveType })
  }

  /*------------------ 表示イベントの計測 ------------------*/
  React.useEffect(() => {
    const items = [
      ...makeSearchConditionTrackingData(watchAllFields, t),
      ...makeSearchScheduleTrackingData(watchAllFields),
    ]
    const label = `件数：${props.sittersTotalCount}件`
    const group = makeSearchTrackingGroup(watchAllFields, t)
    trackEvent({ ...eventIds.SEARCH_RESULT_VIEW, label, group, items }, user)
  }, [true]) // 1回だけ計測したいので、固定値を設定

  return (
    <searchEventIdContext.Provider value={eventIds}>
      <trackingUserContext.Provider value={user}>
        <Presenter
          {...props}
          modalType={modalType}
          searchResultFormSummariesFlex={searchResultFormSummariesFlex}
          sittersViewFromCount={sittersViewFromCount}
          sittersViewToCount={sittersViewToCount}
          searchState={watchAllFields}
          getValues={getValues}
          setModal={setModal}
          submitHandler={handleSubmit}
          searchOrderClickHandler={searchOrderClickHandler}
          searchOrderChangeHandler={searchOrderChangeHandler}
          sortOrderList={sortOrderList}
        />
      </trackingUserContext.Provider>
    </searchEventIdContext.Provider>
  )
}

export default SearchResultForm
